import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Plus, 
  ChevronDown, 
  Grid3X3, 
  List, 
  ArrowUpDown, 
  Cloud, 
  Edit, 
  Trash2, 
  Share2,
  RefreshCw,
  Upload,
  AlertCircle,
  CheckCircle,
  Loader
} from "lucide-react";
import ApiService from '../../services/apiService';
import { toast } from 'react-toastify';

const UploadImagePage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTags, setSelectedTags] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("date");
  const [visuals, setVisuals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [generatedVisuals, setGeneratedVisuals] = useState([]);
  
  const fileInputRef = useRef(null);
  const apiService = new ApiService();

  // Load existing visuals from API
  const loadVisuals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Generate some sample visuals using the vidya-agent
      const sampleConcepts = ['water cycle', 'solar system', 'plant parts', 'human body', 'food chain'];
      const visualPromises = sampleConcepts.map(concept => 
        apiService.makeApiCall('vidya', '/api/generate-visual', { 
          concept: concept,
          gradeLevel: '3-5',
          language: 'English'
        }, 'POST')
          .catch(err => ({ error: err.message, concept }))
      );
      
      const results = await Promise.allSettled(visualPromises);
      const successfulVisuals = results
        .filter(result => result.status === 'fulfilled' && result.value?.status === 'success')
        .map((result, index) => ({
          id: index + 1,
          title: `${sampleConcepts[index]} Diagram`,
          image: result.value.data.downloadUrl || result.value.data.fileUrl,
          imageBase64: result.value.data.imageBase64,
          grade: "Grade 3-5",
          subject: "Science",
          type: "AI Generated",
          tags: [sampleConcepts[index].replace(' ', '-'), "diagram"],
          concept: sampleConcepts[index],
          source: result.value.data.source || 'AI Generated',
          timestamp: new Date().toISOString()
        }));
      
      // Add fallback visuals if API calls fail
      const fallbackVisuals = [
        {
          id: Date.now() + 1,
          title: "Water Cycle Diagram",
          image: "/FRAME.png",
          grade: "Grade 3",
          subject: "Science",
          type: "Educational",
          tags: ["water", "cycle", "diagram"],
          concept: "water cycle"
        },
        {
          id: Date.now() + 2,
          title: "Solar System",
          image: "/FRAME(1).png",
          grade: "Grade 4",
          subject: "Science",
          type: "Educational",
          tags: ["space", "planets", "solar"],
          concept: "solar system"
        },
        {
          id: Date.now() + 3,
          title: "Plant Parts",
          image: "/FRAME(2).png",
          grade: "Grade 3",
          subject: "Biology",
          type: "Educational",
          tags: ["plants", "biology", "parts"],
          concept: "plant structure"
        }
      ];
      
      const allVisuals = successfulVisuals.length > 0 ? successfulVisuals : fallbackVisuals;
      setVisuals(allVisuals);
      setGeneratedVisuals(successfulVisuals);
      
      if (successfulVisuals.length > 0) {
        toast.success(`Loaded ${successfulVisuals.length} AI-generated visuals`);
      }
    } catch (err) {
      console.error('Failed to load visuals:', err);
      setError(err.message);
      toast.error('Failed to load visuals');
    } finally {
      setLoading(false);
    }
  };

  // Generate new visual from concept
  const generateVisual = async (concept) => {
    try {
      setIsUploading(true);
      setUploadProgress(20);
      
      const response = await apiService.makeApiCall('vidya', '/api/generate-visual', {
        concept: concept,
        gradeLevel: '3-5',
        language: 'English'
      }, 'POST');
      setUploadProgress(80);
      
      if (response.status === 'success') {
        const newVisual = {
          id: Date.now(),
          title: `${concept} Diagram`,
          image: response.data.downloadUrl || response.data.fileUrl,
          imageBase64: response.data.imageBase64,
          grade: "Grade 3-5",
          subject: "Science",
          type: "AI Generated",
          tags: [concept.replace(' ', '-'), "diagram", "ai-generated"],
          concept: concept,
          source: response.data.source,
          timestamp: new Date().toISOString()
        };
        
        setVisuals(prev => [newVisual, ...prev]);
        setGeneratedVisuals(prev => [newVisual, ...prev]);
        setUploadProgress(100);
        toast.success(`Generated visual for "${concept}"`);
      } else {
        throw new Error('Failed to generate visual');
      }
    } catch (err) {
      console.error('Failed to generate visual:', err);
      toast.error(`Failed to generate visual for "${concept}"`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Analyze uploaded image using vidyarthi-agent
  const analyzeUploadedImage = async (file) => {
    try {
      setIsUploading(true);
      setUploadProgress(30);
      
      // Convert file to base64 for analysis
      const formData = new FormData();
      formData.append('image', file);
      
      // Use vidyarthi-agent to analyze the image
      const response = await apiService.makeApiCall('vidyarthi', '/api/analyze-textbook-image', formData, 'POST');
      setUploadProgress(70);
      
      if (response.status === 'success') {
        const analyzedVisual = {
          id: Date.now(),
          title: response.data.topic || file.name,
          image: URL.createObjectURL(file),
          grade: response.data.gradeLevel || "Unknown",
          subject: response.data.subject || "General",
          type: "Uploaded & Analyzed",
          tags: response.data.keyPoints?.slice(0, 3) || ["uploaded"],
          analysis: response.data,
          timestamp: new Date().toISOString()
        };
        
        setVisuals(prev => [analyzedVisual, ...prev]);
        setUploadProgress(100);
        toast.success(`Analyzed and uploaded "${file.name}"`);
      } else {
        throw new Error('Failed to analyze image');
      }
    } catch (err) {
      console.error('Failed to analyze image:', err);
      toast.error(`Failed to analyze "${file.name}"`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        await analyzeUploadedImage(file);
      } else {
        toast.warning(`Skipped "${file.name}" - only image files are supported`);
      }
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        await analyzeUploadedImage(file);
      } else {
        toast.warning(`Skipped "${file.name}" - only image files are supported`);
      }
    }
    
    // Reset input
    e.target.value = '';
  };

  // Quick generate buttons
  const quickGenerateConcepts = [
    'water cycle', 'solar system', 'human skeleton', 'food chain', 
    'plant life cycle', 'digestive system', 'weather patterns', 'cell structure'
  ];

  // Load visuals on component mount
  useEffect(() => {
    loadVisuals();
  }, []);

  // Filter visuals based on search and filters
  const filteredVisuals = visuals.filter(visual => {
    const matchesSearch = searchQuery === '' || 
      visual.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visual.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesGrade = selectedGrade === '' || visual.grade.includes(selectedGrade.replace('grade-', ''));
    const matchesSubject = selectedSubject === '' || visual.subject.toLowerCase() === selectedSubject.toLowerCase();
    
    return matchesSearch && matchesGrade && matchesSubject;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Upload Classroom Visuals</h1>
            <div className="flex items-center gap-4">
              <div className="relative flex-1 sm:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name, tag, or grade..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-80 pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 whitespace-nowrap">
                <Plus className="w-4 h-4" />
                Bulk Upload
              </button>
            </div>
          </div>

          {/* Upload Area */}
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 transition-colors duration-200 cursor-pointer"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleFileSelect}
            >
              <Cloud className="w-16 h-16 text-blue-500 mx-auto mb-4" />
              <p className="text-lg text-gray-600 mb-2">Drag & Drop files here or click to upload</p>
              <p className="text-sm text-gray-500">Accepts: PNG, JPG, PDF</p>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Date</span>
              <div className="relative">
                <select
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
                >
                  <option value="">All Dates</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Grade</span>
              <div className="relative">
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
                >
                  <option value="">All Grades</option>
                  <option value="grade-1">Grade 1</option>
                  <option value="grade-2">Grade 2</option>
                  <option value="grade-3">Grade 3</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Subject</span>
              <div className="relative">
                <select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
                >
                  <option value="">All Subjects</option>
                  <option value="science">Science</option>
                  <option value="math">Math</option>
                  <option value="english">English</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Tags</span>
              <div className="relative">
                <select
                  value={selectedTags}
                  onChange={(e) => setSelectedTags(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
                >
                  <option value="">All Tags</option>
                  <option value="diagram">Diagram</option>
                  <option value="chart">Chart</option>
                  <option value="photo">Photo</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* View Controls */}
          <div className="flex items-center gap-4">
            <div className="flex items-center border border-gray-200 rounded-lg">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 transition-colors duration-200 ${
                  viewMode === "grid" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 transition-colors duration-200 ${
                  viewMode === "list" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Sort</span>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm appearance-none bg-white"
                >
                  <option value="date">Date</option>
                  <option value="name">Name</option>
                  <option value="grade">Grade</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div
              className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}
            >
          {filteredVisuals.map((diagram) => (
            <div
              key={diagram.id}
              className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
            >
              <div className="aspect-video bg-gray-100 relative overflow-hidden">
                <img
                  src={diagram.image || "/placeholder.svg"}
                  alt={diagram.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <button className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors duration-200">
                    <Edit className="w-4 h-4 text-gray-600" />
                  </button>
                  <button className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors duration-200">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                  <button className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors duration-200">
                    <Share2 className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">{diagram.title}</h3>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                    {diagram.grade}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>{diagram.subject}</span>
                  <span>{diagram.type}</span>
                </div>
              </div>
            </div>
          ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadImagePage;